import { useCallback, useEffect, useState } from "react";
import { changeFileAccess } from "@/modules/hub/features/FileManagement/api/changeFileAccess/changeFileAccessApi";
import { uploadFilesystemNodes } from "@/modules/hub/features/FileManagement/api/uploadFilesystemNodes/uploadFilesystemNodesApi";
import { generateThumbnailPreview } from "../../api/generateThumbnailPreview/generateThumbnailPreviewApi";
import { ThumbnailPreview } from "../../api/generateThumbnailPreview/types";

export interface AcceptedResourceThumbnail {
  fileUuid: string;
  publicUrl: string;
  name: string;
}

interface UseGenerateThumbnailPanelArgs {
  isOpen: boolean;
  initialTitle: string;
  initialDescription: string;
  onAccepted: (thumbnail: AcceptedResourceThumbnail) => void;
}

export const useGenerateThumbnailPanel = ({
  isOpen,
  initialTitle,
  initialDescription,
  onAccepted,
}: UseGenerateThumbnailPanelArgs) => {
  const [title, setTitle] = useState(initialTitle);
  const [description, setDescription] = useState(initialDescription);
  const [preview, setPreview] = useState<ThumbnailPreview | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isAccepting, setIsAccepting] = useState(false);

  // Reopening should start from the resource's current text, not the last run's.
  useEffect(() => {
    if (!isOpen) return;

    setTitle(initialTitle);
    setDescription(initialDescription);
    setPreview(null);
  }, [isOpen, initialTitle, initialDescription]);

  const canGenerate =
    title.trim().length > 0 &&
    description.trim().length > 0 &&
    !isGenerating &&
    !isAccepting;

  const generate = useCallback(() => {
    setIsGenerating(true);

    generateThumbnailPreview({ title, description })
      .then((response) => setPreview(response.data))
      .finally(() => setIsGenerating(false));
  }, [title, description]);

  const accept = useCallback(() => {
    if (!preview) return;

    setIsAccepting(true);

    const fileName = buildFileName(title);

    uploadFilesystemNodes([toPngFile(preview.imageBase64, fileName)])
      .then((response) => {
        const uploaded = response.data.files[0];

        return changeFileAccess(uploaded.uuid, true).then((published) =>
          onAccepted({
            fileUuid: uploaded.uuid,
            publicUrl: published.publicUrl ?? "",
            name: uploaded.name,
          }),
        );
      })
      .finally(() => setIsAccepting(false));
  }, [preview, title, onAccepted]);

  return {
    title,
    setTitle,
    description,
    setDescription,
    preview,
    previewSrc: preview ? `data:image/png;base64,${preview.imageBase64}` : null,
    isGenerating,
    isAccepting,
    canGenerate,
    generate,
    accept,
  };
};

const toPngFile = (imageBase64: string, fileName: string): File => {
  const binary = atob(imageBase64);
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return new File([bytes], fileName, { type: "image/png" });
};

const buildFileName = (title: string): string => {
  const slug = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  return `${slug || "resource-thumbnail"}.png`;
};
